import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { workAuthorPublisherService } from "../services/workAuthorPublisherService";
import { workAuthorPublisherDisplayId } from "./WorkAuthorPublisherCommonStore";
import { WorkAuthorPublisher } from "../../../api/graphql";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";
import { publisherService } from "../../publisher/services/publisherService";
import { InitializeParams } from "../../../store/types";

export class WorkAuthorPublisherViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = workAuthorPublisherService;
  workAuthorPublisherInstance: WorkAuthorPublisher | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  publisherService = publisherService;
  publisherSearchValue: string = "";
  publisherIdValue: string = "";
  publisherSelectedValue: { displayId: string; id: string } | null = null;
  percentageValue: number = 0;
  durationInYearsValue: number | undefined = undefined;
  autoRenewalYearsValue: number | undefined = undefined;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadWorkAuthorPublisherById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.workAuthorPublisherOne({ id });
    this.workAuthorPublisherInstance = result.workAuthorPublisherOne;
    if (this.workAuthorPublisherInstance) {
      this.loadValues(this.workAuthorPublisherInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.workAuthorPublisherInstance) {
      return workAuthorPublisherDisplayId(
        this.workAuthorPublisherInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkAuthorPublisherSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "publisher") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.publisherOne({
          id: params.returnData.resultId,
        });
        const publisher = result.publisherOne;
        this.publisherSelectedValue = publisher;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get publisherValue() {
    if (this.publisherSelectedValue) {
      return {
        id: this.publisherSelectedValue.id,
        displayId: publisherDisplayId(
          this.publisherSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.workAuthorPublisherInstance = null;
    this.publisherSearchValue = "";
    this.publisherIdValue = "";
    this.percentageValue = 0;
    this.durationInYearsValue = undefined;
    this.autoRenewalYearsValue = undefined;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelWorkAuthorPublisher() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editWorkAuthorPublisher() {
    const id = this.workAuthorPublisherInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/workAuthorPublisher/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printWorkAuthorPublisher(handlePrint: () => void): void {
    handlePrint();
  }

  *closeWorkAuthorPublisher(): any {
    yield this.close();
  }

  loadValues(instance: WorkAuthorPublisher) {
    this.idValue = instance.id;
    this.publisherSelectedValue = instance.publisher;
    this.percentageValue = instance.percentage;
    this.durationInYearsValue = instance.durationInYears || undefined;
    this.autoRenewalYearsValue = instance.autoRenewalYears || undefined;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadWorkAuthorPublisherById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
